import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import TabNavigation from '../components/TabNavigation';
import { formatDate } from '../utils/dateUtils';
import { severityToLabel, severityBadgeClass } from '../utils/severity';
import { formatAssignee } from '../utils/assignee';
import { INCIDENT_STATUSES } from '../constants/incidentStates';
import { INCIDENT_TYPES, incidentTypeToLabel } from '../constants/incidentTypes';
import ExcelExportModal from '../components/ExcelExportModal';
import { useAlert } from '../context/AlertContext';
import { useSite } from '../context/SiteContext';
import { usePermissions } from '../context/PermissionContext';
import { useListFilters } from '../hooks/useListFilters';
import { useIsMobile } from '../hooks/useIsMobile';

// Default filter set. Shared by the initial state, the URL/storage restore
// logic and the Reset All button so the three can never drift apart.
const DEFAULT_INCIDENT_FILTERS = {
  status: 'All',
  severity: 'All',
  assignee: 'All',
  lti: 'All',
  category: 'All',
  department: 'All',
  dateFrom: '',
  dateTo: '',
  search: ''
};

// Canonical incidents list page. Targets the /incidents API (post-split
// from /hazards). The old HazardsList.js continues to read from /hazards
// during the Phase 2 soak period.
const IncidentsList = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useAlert();
  const { activeSiteId } = useSite();
  const { getResourcePermissions } = usePermissions();
  // Resource-specific gate (CMP-01 / CMP-27). hasPermission() also matches
  // module_slug, so it can show a button the API then refuses; this mirrors
  // the backend's incidents:<action> check exactly.
  const canIncidents = (action) =>
    getResourcePermissions('incidents').some(p => p.action === action);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filter + pagination state lives in useListFilters, which mirrors it into
  // the URL and localStorage. Deep links (e.g. the dashboard Incident Snapshot
  // cards linking to /incidents?status=Open&date_from=...) still win over the
  // remembered set; unknown values fall back to 'All' so a stale link can't
  // leave a dropdown in an unrepresentable state.
  const {
    filters,
    queryFilters,
    setFilter: handleFilterChange,
    resetFilters,
    clearIfInvalid,
    page,
    setPage,
    pageSize,
    setPageSize,
  } = useListFilters({
    storageKey: 'incidents',
    defaults: DEFAULT_INCIDENT_FILTERS,
    sanitize: {
      status: (v, fallback) => (Object.values(INCIDENT_STATUSES).includes(v) ? v : fallback),
    },
  });
  const isMobile = useIsMobile();
  const [departments, setDepartments] = useState([]);

  useEffect(() => {
    axios.get(`${API}/departments`)
      .then(res => setDepartments(res.data.departments || []))
      .catch(() => setDepartments([]));
  }, []);
  const [total, setTotal] = useState(0);
  const [aggregations, setAggregations] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [csvExportLoading, setCsvExportLoading] = useState(false);

  const fetchAggregations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (activeSiteId) params.append('site_id', activeSiteId);
      if (queryFilters.dateFrom) params.append('date_from', queryFilters.dateFrom);
      if (queryFilters.dateTo) params.append('date_to', queryFilters.dateTo);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/dashboard/safety/aggregations${qs}`);
      setAggregations(response.data);
    } catch (error) {
      console.error('Failed to fetch aggregations:', error);
    }
  }, [activeSiteId, queryFilters.dateFrom, queryFilters.dateTo]);

  const fetchIncidents = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeSiteId) params.append('site_id', activeSiteId);
      params.append('page', String(page));
      params.append('limit', String(pageSize));
      if (queryFilters.status !== 'All') params.append('status', queryFilters.status);
      if (queryFilters.severity !== 'All') params.append('severity', queryFilters.severity);
      if (queryFilters.assignee !== 'All') params.append('assignee', queryFilters.assignee);
      if (queryFilters.category !== 'All') params.append('hazard_type', queryFilters.category);
      if (queryFilters.department !== 'All') params.append('department_id', queryFilters.department);
      if (queryFilters.lti === 'LTI Only') params.append('is_lti', 'true');
      else if (queryFilters.lti === 'Non-LTI') params.append('is_lti', 'false');
      if (queryFilters.dateFrom) params.append('date_from', queryFilters.dateFrom);
      if (queryFilters.dateTo) params.append('date_to', queryFilters.dateTo);
      if (queryFilters.search?.trim()) params.append('search', queryFilters.search.trim());

      const response = await axios.get(`${API}/incidents?${params.toString()}`);
      // Paginated envelope when `page` is sent
      if (response.data && Array.isArray(response.data.items)) {
        setIncidents(response.data.items);
        setTotal(Number(response.data.total) || 0);
      } else if (Array.isArray(response.data)) {
        // Fallback if backend doesn't recognise `page` (older build)
        setIncidents(response.data);
        setTotal(response.data.length);
      } else {
        console.error('Unexpected incidents response shape:', response.data);
        setIncidents([]);
        setTotal(0);
      }
    } catch (error) {
      console.error('Failed to fetch incidents:', error);
      showError(`Failed to fetch incidents: ${error.response?.data?.detail || error.message}`);
      setIncidents([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeSiteId, page, pageSize, queryFilters, showError]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  useEffect(() => {
    fetchAggregations();
  }, [fetchAggregations]);

  // Switching site resets to page 1 so we don't land on an empty page.
  // Filter changes reset the page inside useListFilters' setFilter — doing it
  // here too would clobber a page restored from the URL on mount.
  useEffect(() => {
    setPage(1);
  }, [activeSiteId, setPage]);

  // A department remembered under a different site may not exist here.
  useEffect(() => {
    if (!departments.length) return;
    clearIfInvalid('department', (v) => departments.some(d => String(d.id) === String(v)));
  }, [departments, clearIfInvalid]);

  // Server already filtered + paginated — keep a derived alias so the
  // existing render code (which referenced `filteredIncidents`) keeps working.
  const filteredIncidents = incidents;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const handleExportCsv = async () => {
    setCsvExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeSiteId) params.append('site_id', activeSiteId);
      if (queryFilters.category !== 'All') params.append('category', queryFilters.category);
      if (queryFilters.status !== 'All') params.append('status', queryFilters.status);
      if (queryFilters.severity !== 'All') params.append('severity', queryFilters.severity);
      if (queryFilters.dateFrom) params.append('date_from', queryFilters.dateFrom);
      if (queryFilters.dateTo) params.append('date_to', queryFilters.dateTo);
      if (queryFilters.search) params.append('search', queryFilters.search);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const response = await axios.get(`${API}/export/csv${qs}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Saarv_Incidents_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      showSuccess('CSV file downloaded successfully!');
    } catch (error) {
      console.error('Failed to export CSV:', error);
      showError('Failed to export CSV. Please try again.');
    } finally {
      setCsvExportLoading(false);
    }
  };

  const handleExportExcel = async () => {
    setShowExportModal(true);
    setExportLoading(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const excelParams = activeSiteId ? `?site_id=${activeSiteId}` : '';
      const response = await axios.get(`${API}/export/excel${excelParams}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Saarv_Incidents_Report_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setShowExportModal(false);
      setTimeout(() => showSuccess('Excel file downloaded successfully!'), 500);
    } catch (error) {
      console.error('Failed to export:', error);
      setShowExportModal(false);
      if (error.response?.status === 401) {
        showError('Session expired. Please login again.');
        window.location.href = '/login';
      } else {
        showError('Failed to export data. Please try again.');
      }
    } finally {
      setExportLoading(false);
    }
  };

  // Search has its own always-visible box, so it is not counted here.
  const activeFilterCount = Object.entries(DEFAULT_INCIDENT_FILTERS)
    .filter(([key, fallback]) => key !== 'search' && filters[key] !== fallback)
    .length;

  // Collapsed by default so the list is the first thing on the page, but open
  // from the start when a deep link or a remembered filter is already
  // narrowing it — a short list has to explain itself. Seeded through
  // useState rather than an effect: the initial value is used on the first
  // render only, which is the behaviour wanted, and it avoids a dependency
  // array (this project cannot resolve react-hooks/exhaustive-deps, so any
  // deps list the rule wants to flag fails the dev build).
  const [showFilters, setShowFilters] = useState(activeFilterCount > 0);
  const getUniqueValues = (key) => {
    // The active selection is always kept selectable, even when the current
    // result set no longer contains it. Filters are remembered across visits,
    // so a restored assignee that now matches nothing would otherwise leave the
    // dropdown showing 'All' while the list stayed filtered.
    const selected = filters[key];
    const values = new Set();
    try {
      (incidents || []).forEach(i => { const v = i?.[key]; if (v) values.add(v); });
    } catch (error) {
      // Malformed row data — fall back to just the active selection.
    }
    if (selected && selected !== 'All') values.add(selected);
    return ['All', ...values];
  };

  const SEVERITY_FILTER_OPTIONS = ['All', 'Low', 'Medium', 'High', 'Critical'];
  const STATUS_FILTER_OPTIONS = [
    'All',
    INCIDENT_STATUSES.DRAFT,
    INCIDENT_STATUSES.OPEN,
    INCIDENT_STATUSES.IN_PROGRESS,
    INCIDENT_STATUSES.OVERDUE,
    INCIDENT_STATUSES.CLOSED,
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Closed':
        return 'bg-green-100 text-green-700';
      case 'Overdue':
        return 'bg-red-100 text-red-700';
      case 'In-Progress':
        return 'bg-blue-100 text-blue-700';
      case 'Open':
        return 'bg-yellow-100 text-yellow-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Full-page spinner only on the *initial* load (no rows yet). Page / filter
  // changes still flip `loading=true` but keep the existing table visible so
  // pagination + filter UX doesn't flash.
  if (loading && incidents.length === 0 && total === 0) {
    return (
      <div className="min-h-screen bg-gray-50">
        <TabNavigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
            <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-[#1B5C94]"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6" data-testid="incidents-list-page">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-800">INCIDENTS LIST</h1>
              <p className="text-gray-600 mt-1">
                {total} incident(s) match
                {aggregations?.lti_total != null && (
                  <>
                    {' • '}
                    <span className="font-semibold text-red-600">
                      {aggregations.lti_total} LTI(s)
                    </span>
                  </>
                )}
              </p>
            </div>
            <div className="flex flex-wrap gap-3 mt-4 md:mt-0 items-center">
              {canIncidents('create') && (
                <button
                  onClick={() => navigate('/report')}
                  className="bg-[#1B5C94] text-white px-6 py-2 rounded-lg font-semibold hover:bg-[#3AC0DB] transition-colors"
                  data-testid="report-incident-button"
                >
                  + Report Incident
                </button>
              )}
              {canIncidents('export') && (
                <button
                  onClick={handleExportExcel}
                  disabled={exportLoading}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors disabled:bg-gray-400"
                  data-testid="export-excel-button"
                >
                  {exportLoading ? 'Exporting...' : 'Export Excel'}
                </button>
              )}
              {canIncidents('export') && (
                <button
                  onClick={handleExportCsv}
                  disabled={csvExportLoading}
                  className="bg-teal-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-teal-700 transition-colors disabled:bg-gray-400"
                  data-testid="export-csv-button"
                >
                  {csvExportLoading ? 'Exporting...' : 'Export CSV'}
                </button>
              )}
            </div>
          </div>

          {/* Search and filters in ONE bar, with the filter grid collapsed by
              default. The list is what the page is for; it used to start 737px
              down, below summary cards and an always-open eight-field panel. */}
          <div className="bg-white rounded-xl shadow-md p-4">
            <div className="flex flex-wrap items-center gap-3">
              <input
                type="text"
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                placeholder="Search by ID, title, description, or location..."
                className="flex-1 min-w-[240px] px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94] text-sm"
                data-testid="search-input"
              />
              <button
                type="button"
                onClick={() => setShowFilters(v => !v)}
                aria-expanded={showFilters}
                className={`shrink-0 px-4 py-2.5 rounded-lg text-sm font-semibold border transition-colors ${
                  activeFilterCount > 0
                    ? 'bg-[#1B5C94] text-white border-[#1B5C94]'
                    : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                }`}
                data-testid="toggle-filters"
              >
                Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
                <span className="ml-2">{showFilters ? '▴' : '▾'}</span>
              </button>
              {activeFilterCount > 0 && (
                <button
                  onClick={resetFilters}
                  className="shrink-0 text-sm text-[#1B5C94] hover:underline font-semibold"
                >
                  Reset All
                </button>
              )}
            </div>

            {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-100">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Incident Type</label>
                <select value={filters.category} onChange={(e) => handleFilterChange('category', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]" data-testid="category-filter">
                  {['All', ...INCIDENT_TYPES].map(cat => <option key={cat} value={cat}>{cat}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Department</label>
                <select value={filters.department} onChange={(e) => handleFilterChange('department', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]">
                  <option value="All">All</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
                <select value={filters.status} onChange={(e) => handleFilterChange('status', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]" data-testid="status-filter">
                  {STATUS_FILTER_OPTIONS.map(status => <option key={status} value={status}>{status}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Severity</label>
                <select value={filters.severity} onChange={(e) => handleFilterChange('severity', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]" data-testid="severity-filter">
                  {SEVERITY_FILTER_OPTIONS.map(severity => <option key={severity} value={severity}>{severity}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Assigned To</label>
                <select value={filters.assignee} onChange={(e) => handleFilterChange('assignee', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]" data-testid="assignee-filter">
                  {getUniqueValues('assignee').map(assignee => <option key={assignee} value={assignee}>{assignee}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Lost Time Injury</label>
                <select value={filters.lti} onChange={(e) => handleFilterChange('lti', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]" data-testid="lti-filter">
                  <option value="All">All Incidents</option>
                  <option value="LTI Only">LTI Only</option>
                  <option value="Non-LTI">Non-LTI</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Date From</label>
                <input type="date" value={filters.dateFrom} onChange={(e) => handleFilterChange('dateFrom', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]" data-testid="date-from-filter" />
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Date To</label>
                <input type="date" value={filters.dateTo} onChange={(e) => handleFilterChange('dateTo', e.target.value)} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1B5C94]" data-testid="date-to-filter" />
              </div>
            </div>
            )}
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            {!isMobile && (
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gradient-to-r from-[#1B5C94] to-[#3AC0DB] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left text-sm font-bold">ID</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Title</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Incident Type</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Severity</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Status</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Assigned To</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Reported Date</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Due Date</th>
                    <th className="px-6 py-4 text-left text-sm font-bold">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredIncidents.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center text-gray-500">No incidents found</td>
                    </tr>
                  ) : (
                    filteredIncidents.map((incident, index) => (
                      <tr key={incident?.id || index} className="hover:bg-gray-50 transition-colors" data-testid={`incident-row-${incident?.id}`}>
                        <td className="px-6 py-4 text-sm text-gray-700 font-mono">{incident?.incident_number || (index + 1)}</td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <div>
                              <div className="text-sm font-semibold text-gray-800">{incident?.title || 'Untitled'}</div>
                              <div className="text-xs text-gray-500 mt-1">{incident?.hazard_type || 'N/A'}</div>
                            </div>
                            {incident?.is_lti && (
                              <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-bold bg-red-100 text-red-800 border border-red-300">🚨 LTI</span>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-700">{incidentTypeToLabel(incident?.incident_type)}</td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${severityBadgeClass(incident?.severity)}`}>{severityToLabel(incident?.severity) || 'N/A'}</span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(incident?.status)}`}>{incident?.status || 'N/A'}</span>
                        </td>
                        {/* formatAssignee reads ONLY `assignee`. The previous
                            fallback to reported_by_name rendered the *reporter*
                            under an "Assigned To" heading, so an unassigned
                            incident filed by an admin displayed as "Admin" here
                            while the detail view said "Not assigned". */}
                        <td className="px-6 py-4 text-sm text-gray-700">{formatAssignee(incident)}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{(incident?.reported_date || incident?.created_at) ? formatDate(incident.reported_date || incident.created_at) : 'N/A'}</td>
                        <td className="px-6 py-4 text-sm text-gray-700">{incident?.due_date ? formatDate(incident.due_date) : 'N/A'}</td>
                        <td className="px-6 py-4">
                          <button
                            onClick={() => navigate(`/incidents/${incident?.id}`)}
                            className="bg-[#1B5C94] text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-[#3AC0DB] transition-colors"
                            data-testid={`view-incident-${incident?.id}`}
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            )}

            {isMobile && (
            <div className="md:hidden p-4 space-y-4">
              {filteredIncidents.length === 0 ? (
                <div className="p-12 text-center text-gray-500">
                  <p className="text-lg font-semibold">No incidents found</p>
                  <p className="text-sm mt-2">Try adjusting your filters</p>
                </div>
              ) : (
                filteredIncidents.map((incident, index) => (
                  <div key={incident?.id || index} className="bg-gray-50 rounded-lg p-4 space-y-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="text-sm font-bold text-gray-500 font-mono">{incident?.incident_number || `#${index + 1}`}</div>
                        <div className="text-lg font-semibold text-gray-800 mt-1">{incident?.title || 'Untitled'}</div>
                        <div className="text-sm text-gray-600 mt-1">{incident?.hazard_type || 'N/A'}</div>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${severityBadgeClass(incident?.severity)}`}>{severityToLabel(incident?.severity) || 'N/A'}</span>
                      <span className={`px-3 py-1 rounded-full text-xs font-bold ${getStatusColor(incident?.status)}`}>{incident?.status || 'N/A'}</span>
                      {incident?.is_lti && (
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-bold bg-red-100 text-red-800 border border-red-300">🚨 LTI</span>
                      )}
                    </div>
                    <div className="text-sm text-gray-700">
                      <div><span className="font-semibold">Incident Type:</span> {incidentTypeToLabel(incident?.incident_type)}</div>
                      <div><span className="font-semibold">Assigned To:</span> {formatAssignee(incident)}</div>
                      <div><span className="font-semibold">Reported:</span> {(incident?.reported_date || incident?.created_at) ? formatDate(incident.reported_date || incident.created_at) : 'N/A'}</div>
                      <div><span className="font-semibold">Due:</span> {incident?.due_date ? formatDate(incident.due_date) : 'N/A'}</div>
                    </div>
                    <button
                      onClick={() => navigate(`/incidents/${incident?.id}`)}
                      className="w-full bg-[#1B5C94] text-white py-2 rounded-lg text-sm font-semibold hover:bg-[#3AC0DB] transition-colors"
                    >
                      View Details
                    </button>
                  </div>
                ))
              )}
            </div>
            )}

            {/* Pagination footer */}
            {total > 0 && (
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 px-4 sm:px-6 py-3 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600">
                  Showing{' '}
                  <span className="font-semibold">
                    {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)}
                  </span>{' '}
                  of <span className="font-semibold">{total}</span>
                </div>
                <div className="flex items-center gap-3">
                  <label className="text-sm text-gray-600">
                    Rows&nbsp;
                    <select
                      value={pageSize}
                      onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-[#1B5C94]"
                      data-testid="page-size-select"
                    >
                      {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                    </select>
                  </label>
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setPage(1)}
                      disabled={page === 1 || loading}
                      className="px-3 py-2 sm:px-2 sm:py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="page-first"
                    >
                      « First
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1 || loading}
                      className="px-3 py-2 sm:px-2 sm:py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="page-prev"
                    >
                      ‹ Prev
                    </button>
                    <span className="px-3 text-sm text-gray-700">
                      Page <span className="font-semibold">{page}</span> / {totalPages}
                    </span>
                    <button
                      type="button"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page >= totalPages || loading}
                      className="px-3 py-2 sm:px-2 sm:py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="page-next"
                    >
                      Next ›
                    </button>
                    <button
                      type="button"
                      onClick={() => setPage(totalPages)}
                      disabled={page >= totalPages || loading}
                      className="px-3 py-2 sm:px-2 sm:py-1 text-sm border border-gray-300 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid="page-last"
                    >
                      Last »
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <ExcelExportModal isOpen={showExportModal} onClose={() => setShowExportModal(false)} />
      </div>
    </div>
  );
};

export default IncidentsList;
